import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useDash } from "@/pages/dashboard/ctx";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ArrowUpRight, ChevronDown, Loader2, MessageSquare, Phone, ClipboardList, Home, BellOff } from "lucide-react";
import { ago, fmtDay } from "./pulse";

const ICON = { conversation: MessageSquare, call: Phone, request: ClipboardList, household: Home, optout: BellOff };

function Item({ it, go }) {
  const [more, setMore] = useState(false);
  const Icon = ICON[it.kind] || MessageSquare;
  const convs = it.conversations || [];
  return (
    <li className="border-t border-[#F3EFE9] first:border-0" data-testid={`pulse-drill-item-${it.id}`}>
      <div className="grid grid-cols-[18px_1fr_auto] gap-3 py-3">
        <Icon className="h-[18px] w-[18px] text-[#8C827A] mt-0.5" strokeWidth={1.8} />
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap text-[13px]">
            <span className="font-bold text-[#1C1917]">{it.who}</span>
            {it.unit && <span className="text-[#8C827A]">· {it.unit}</span>}
            <span className="text-[11px] text-[#A8A29E] font-mono">{ago(it.when)}</span>
          </div>
          {it.note && <p className="mt-0.5 text-[13px] text-[#57534E] leading-snug break-words">{it.kind === "conversation" ? <>“{it.note}”</> : it.note}</p>}
          {(it.tags || []).length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {it.tags.map((t) => <span key={t} className="rounded-full bg-[#F3EFE9] px-2 py-0.5 text-[10px] font-bold text-[#57534E]">{t}</span>)}
              {it.request && (
                <button type="button" onClick={() => go(it.request.to)} data-testid={`pulse-drill-request-${it.id}`}
                  className="rounded-full border border-[#E8E2D9] px-2 py-0.5 text-[10px] font-bold text-[#1C1917] hover:border-ink">request · {it.request.status?.replace("_", " ")} →</button>
              )}
            </div>
          )}
          {convs.length > 0 && (
            <button type="button" onClick={() => setMore((m) => !m)} data-testid={`pulse-drill-expand-${it.id}`}
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-[#57534E] hover:text-[#1C1917]">
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${more ? "rotate-180" : ""}`} /> {more ? "Hide" : "Show"} the conversations
            </button>
          )}
          {more && convs.length > 0 && (
            <ul className="mt-1.5 space-y-1 border-l-2 border-[#E8E2D9] pl-3">
              {convs.map((c) => (
                <li key={c.id} className="flex items-baseline gap-2 text-[12px]">
                  <span className="font-mono text-[10px] text-[#A8A29E] shrink-0">{fmtDay(c.when?.slice(0, 10))}</span>
                  <span className="text-[#57534E] truncate">{c.note ? `“${c.note}”` : "conversation"}</span>
                  <button type="button" onClick={() => go(c.to)} className="ml-auto shrink-0 font-bold text-[#1C1917] hover:underline" data-testid={`pulse-drill-conv-${c.id}`}>Open</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button type="button" onClick={() => go(it.to)} data-testid={`pulse-drill-open-${it.id}`}
          className="self-start inline-flex items-center gap-0.5 rounded-full border border-[#E8E2D9] bg-white px-2.5 py-1 text-[11px] font-bold text-[#1C1917] hover:border-ink hover:bg-[#FAF8F5]">
          Open <ArrowUpRight className="h-3 w-3" />
        </button>
      </div>
    </li>
  );
}

// Slide-over: the exact conversations / requests / households behind one Pulse signal this month,
// each linked into the tool that resolves it (Conversations, Requests, Teach the AI, Roster).
export function PulseDrill({ tenantId, signal, name, onClose }) {
  const navigate = useNavigate();
  const dash = useDash();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!tenantId || !signal) return;
    setData(null); setErr(null);
    api.get(`/tenants/${tenantId}/community-pulse/drivers/${signal}`).then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load the records"));
  }, [tenantId, signal]);
  const go = (to) => {
    onClose();
    if (dash?.enterCommunity && dash?.tenantId !== tenantId) dash.enterCommunity(tenantId, to);
    else navigate(to);
  };
  const open = !!signal;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-[560px] p-0 flex flex-col" data-testid="pulse-drill">
        <SheetHeader className="px-4 sm:px-6 pt-5 pb-3.5 border-b border-[#F3EFE9] text-left">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A8378]">{name ? `${name} · ` : ""}behind the number</div>
          <SheetTitle className="font-heading text-xl tracking-tight" data-testid="pulse-drill-title">{data?.label || "Loading…"}</SheetTitle>
          <SheetDescription className="text-[13px] text-[#57534E]" data-testid="pulse-drill-lead">
            {data?.lead || data?.hint || ""}{data?.window ? ` · ${fmtDay(data.window[0].slice(0, 10))} → ${fmtDay(data.window[1].slice(0, 10))}` : ""}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {err && <div className="text-sm text-[#B91C1C]" data-testid="pulse-drill-error">{err}</div>}
          {!data && !err && <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#8C827A]" /></div>}
          {data && (
            <>
              {data.links?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="pulse-drill-links">
                  {data.links.map((l) => (
                    <button key={l.to} type="button" onClick={() => go(l.to)} data-testid={`pulse-drill-link-${l.to.replace(/[^a-z]+/gi, "-")}`}
                      className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-[12px] font-bold text-white hover:bg-[#292524]">
                      {l.label} <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                  ))}
                </div>
              )}
              {data.items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#E8E2D9] px-4 py-8 text-center text-sm text-[#8C827A]" data-testid="pulse-drill-empty">Nothing behind this signal this month.</div>
              ) : (
                <>
                  <div className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#8C827A] mb-1">
                    {data.items.length < data.count ? `Showing ${data.items.length} of ${data.count}` : `${data.count} record${data.count === 1 ? "" : "s"}`}
                  </div>
                  <ul data-testid="pulse-drill-list">{data.items.map((it) => <Item key={`${it.kind}-${it.id}`} it={it} go={go} />)}</ul>
                </>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
